import React, { useState, useEffect } from 'react';
import { FaFireExtinguisher, FaBell, FaShieldAlt, FaCog, FaCheckCircle, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';

// A custom hook to get the window size for responsive inline styles
const useWindowSize = () => {
  const [size, setSize] = useState([window.innerWidth, window.innerHeight]);
  useEffect(() => {
    const handleResize = () => setSize([window.innerWidth, window.innerHeight]);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return size;
};


// ========== The Main Component Starts Here ==========
const FireSafetyPage = () => {
  const [width] = useWindowSize();
  const isMobile = width < 768;

  // ========== INLINE CSS STYLES OBJECT ==========
  const styles = {
    page: {
      fontFamily: "'Poppins', sans-serif",
      color: '#333',
      backgroundColor: '#fff',
      overflow: 'hidden',
    },
    heroSection: {
      position: 'relative',
     // Use a relevant background
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      textAlign: 'center',
      
    },
    heroOverlay: {
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
    
    },
    heroContent: {
      position: 'relative',
      zIndex: 1,
      padding: '2rem 0',
    },
    heroTitle: {
      fontSize: isMobile ? '2.5rem' : '4rem',
      fontWeight: 'bolder',
      textShadow: '2px 2px 8px rgba(0,0,0,0.6)',
      color: '#F37822', // Orange from brochure
    },
    heroSubtitle: {
        fontSize: isMobile ? '1.5rem' : '2.5rem',
        fontWeight: 'bold'
    },
    section: {
      padding: isMobile ? '60px 0' : '50px 0',
    },
    sectionLight: {
      padding: isMobile ? '60px 0' : '80px 0',
      backgroundColor: '#f8f9fa',
    },
    sectionHeader: {
      textAlign: 'center',
      marginBottom: isMobile ? '40px' : '60px',
    },
    sectionTitle: {
      fontSize: isMobile ? '2rem' : '2.5rem',
      fontWeight: 'bold',
      marginBottom: '1rem',
    },
    sectionSubtitle: {
      textTransform: 'uppercase',
      fontWeight: 'bold',
      color: '#b22222', // Firebrick Red
      letterSpacing: '2px',
    },
    // --- Custom styles for brochure content ---
    serviceList: {
        listStyle: 'none',
        paddingLeft: '0'
    },
    serviceListItem: {
        fontSize: '1.1rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center'
    },
    tableResponsive: {
        display: 'block',
        width: '100%',
        overflowX: 'auto',
    },
    productTable: {
        width: '100%',
        marginBottom: '1rem',
        color: '#212529',
        borderCollapse: 'collapse',
    },
    tableHeader: {
        backgroundColor: '#F37822',
        color: 'white',
    },
    ctaSection: {
      position: 'relative',
      padding: '100px 0',
      color: 'white',
      textAlign: 'center',
    },
    ctaOverlay: {
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: '#F37822',
      clipPath: 'polygon(0 15%, 100% 0, 100% 85%, 0 100%)',
    },
    ctaButton: {
      borderRadius: '30px',
      padding: '15px 40px',
      fontWeight: 'bold',
      textDecoration: 'none',
      color: '#b22222',
      backgroundColor: 'white',
      boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
    },
     infoImage: {
      border: '10px solid white',
      transform: 'rotate(2deg)',
      borderRadius: '8px',
      boxShadow: '0 15px 30px rgba(0,0,0,0.1)',
    },
  };

  // --- Data from the Brochure ---
  const services = [
    "Installation and maintenance of fire alarm System & hydrant system.",
    "Sales and servicing fire extinguishers.",
    "Fire risk assessments.",
    "Fire & safety training.",
    "Security System."
  ];

  const abcExtinguishers = [
    { desc: "ABC - 1KG.", bis: 15683, discharge: "6-8", jet: "2-3", pressure: 35 },
    { desc: "ABC - 2KG.", bis: 15683, discharge: "6-8", jet: "2-3", pressure: 35 },
    { desc: "ABC - 4KG.", bis: 15683, discharge: "6-8", jet: "6-7", pressure: 35 },
    { desc: "ABC - 6KG.", bis: 15683, discharge: "10-12", jet: "6-7", pressure: 35 }, 
    { desc: "ABC - 9KG.", bis: 15683, discharge: "12-18", jet: "6-7", pressure: 35 },
  ];

  return (
    <div style={styles.page}>
   
      <section style={styles.heroSection}>
        <div style={styles.heroOverlay}></div>
        <div className="container" style={styles.heroContent}>
          <h1 className='tp-ab-title'>FIRE PROTECTION EQUIPMENT & INSTALLATION</h1>
          <p className="tp-ab-subtitle ">PROTECT YOURSELF AGAINST FIRE</p>
        </div>
      </section>

      {/* ========== WHAT WE DO SECTION ========== */}
      <section style={styles.section}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
                <div style={{textAlign: 'left'}}>
                    <h2 className='tp-ab-title'>What We Do?</h2>
                </div>
                <ul style={styles.serviceList}>
                    {services.map((service, index) => (
                        <li key={index} className='tp-ab-info'>
                            <FaCheckCircle className="me-3" style={{color: '#28a745'}} />
                            {service}
                        </li>
                    ))}
                </ul>
            </div>
            <div className="col-lg-6">
                <p className="tp-ab-info">We are a new blossom in the fire & Safety and the electrical industry with a Well-trained & experienced team. We are passionate about providing a world-class service in the fire protection and Security system.</p>
                <p className="tp-ab-info mt-3">We specialize in navigating property managers and business owners to fulfill their obligations. We carry out a full range of fire protection system installations, supplying quotes and project specifications in order to design and implement the most appropriate and cost-effective solution to your needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== OUR PRODUCTS SECTION ========== */}
      <section style={styles.sectionLight}>
        <div className="container">
            <div style={styles.sectionHeader}>
                <h6 className='tp-ab-title'>Our Equipment</h6>
                <h2 className='tp-ab-subtitle py-3'>Fire Extinguishers & Systems</h2>
            </div>
            
            {/* --- DRY POWDER ABC TYPE --- */}
            <h3 className="mb-4 tp-ab-subtitle">Dry Powder ABC Type Fire Extinguishers (Stored Pressure)</h3>
            <div style={styles.tableResponsive}>
                <table className="table table-striped table-bordered" style={styles.productTable}>
                    <thead style={styles.tableHeader}>
                        <tr>
                            <th>Description</th>
                            <th>BIS spec.</th>
                            <th>Discharge Time (Sec.)</th>
                            <th>Jet Length (Meters)</th>
                            <th>Test Pressure Kgf/cm²</th>
                        </tr>
                    </thead>
                    <tbody>
                        {abcExtinguishers.map((item, index) => (
                            <tr key={index}>
                                <td>{item.desc}</td>
                                <td>{item.bis}</td>
                                <td>{item.discharge}</td>
                                <td>{item.jet}</td>
                                <td>{item.pressure}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- CARBON DI-OXIDE --- */}
            <h3 className="mb-4 tp-ab-subtitle mt-5">Carbon Di-Oxide Fire Extinguishers</h3>
            <div className="row g-4">
                <div className="col-md-6">
                    <h5 className='tp-ab-subtitle'>Application</h5>
                    <p className='tp-ab-info'><strong>Class B Fires:</strong> Fires involving flammable liquids & liquefiable solids like Solvents, Petrol, Diesel, Paints, Rubber, wax, lac & all volatile liquids.</p>
                    <p className='tp-ab-info'><strong>Class C Fires:</strong> Fires involving flammable gases, liquefied flammable gases and electrical equipment's/systems.</p>
                </div>
                <div className="col-md-6">
                    <h5 className='tp-ab-subtitle'>Available Capacity</h5>
                    <p className='tp-ab-info'>2 kg | 3 kg | 4.5 kg | 6.5 kg | 9 kg | 22 kg</p>
                </div>
            </div>

            {/* --- OTHER EQUIPMENT --- */}
            <h3 className="mb-4 tp-ab-subtitle mt-5">Hydrant System Components</h3>
            <div className="row g-4">
                <div className="col-md-6">
                    <h5 className='tp-ab-subtitle'>Single Outlet Landing Valves Type A</h5>
                    <ul className="list-unstyled">
                        <li className='tp-ab-info'><strong>Material:</strong>  Gun metal LTB-2 & Stainless steel</li>
                        <li className='tp-ab-info'><strong>Size:</strong> 63 mm</li>
                        <li className='tp-ab-info'><strong>Specification:</strong> Conforming to IS 5290</li>
                        <li className='tp-ab-info'><strong>Hydraulic Test:</strong> Tested at 21 Kgf/cm²</li>
                    </ul>
                </div>
                <div className="col-md-6">
                    <h5 className='tp-ab-subtitle'>Branch Pipe Nozzle & Hose Coupling</h5>
                     <ul className="list-unstyled">
                        <li className='tp-ab-info'><strong>Material:</strong> Gun metal LTB-2, Stainless steel & Aluminium</li>
                        <li className='tp-ab-info'><strong>Size:</strong> 63 mm</li>
                        <li className='tp-ab-info'><strong>Specification:</strong> Conforming to IS 903</li>
                        <li className='tp-ab-info'><strong>Hydraulic Test:</strong> Tested at 21 Kgf/cm²</li>
                    </ul>
                </div>
            </div>
        </div>
      </section>

      {/* ========== WHY CHOOSE US SECTION ========== */}
      <section style={styles.section}>
         <div className="container">
            <div className="row align-items-center g-5 flex-lg-row-reverse">
                <div className="col-lg-6">
                    {/* The image from the brochure is added here */}
                    <img
                        src="/assets/img/bg/istockphoto-1284506661-612x612.jpg" // Path to the image of detectors
                        alt="Comprehensive Security Systems"
                        style={styles.infoImage}
                    />
                </div>
                <div className="col-lg-6">
                    <div style={{...styles.sectionHeader, textAlign: 'left', marginBottom: '20px'}}>
                        <h6 className='tp-ab-subtitle'>Our Advantage</h6>
                        <h2 className='tp-ab-title'>What makes us different?</h2>
                    </div>
                    <p className="tp-ab-info">
                        We provide a range of services for the design, development, and implementation of comprehensive security programs to a number of our customers; either as part of an integrated services contract or as a stand-alone service. So that you have complete confidence that your security's responsibilities are being discharged competently and professionally.
                    </p>
                </div>
            </div>
        </div>
      </section>
      
    


      {/* ========== CTA SECTION ========== */}
    <section style={styles.ctaSection}>
        <div style={styles.ctaOverlay}></div>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <div className="row justify-content-center">
                <div className="col-lg-8">
                    <h2 className="tp-ab-title mb-4">Need a Fire Safety Solution?</h2>
                    <p className="tp-ab-info py-3 text-white">Contact us for a free quote and consultation. Our experts will help you find the perfect fit for your safety needs.</p>
                    <Link to="/contact" style={styles.ctaButton}>Get a Free Quote</Link>
                </div>
            </div>
        </div>
      </section>
    </div>
  );
};

export default FireSafetyPage;